import React from 'react';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, MessageCircle, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { getBackendUrl } from '../config/api';

/**
 * AuthRequiredScreen - Shown when user tries to access Chitragupt chat without authentication
 * This is a workaround for the blank screen bug after Google auth redirect
 */
const AuthRequiredScreen = ({ onBack, onSignIn }) => {
  // Direct Google Auth - skips intermediate pages
  const handleGoogleSignIn = () => {
    // Store that we want to go to chat after auth
    localStorage.setItem('pending_return_view', 'chat');
    sessionStorage.setItem('authReturnPath', window.location.pathname);  // Save current path
    sessionStorage.setItem('authReturnView', 'chat');
    sessionStorage.setItem('resumeChatAfterAuth', 'true');
    
    // Redirect to login page with Google One Tap
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--cosmic-gradient)' }}>
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-10">
        {/* Lock Icon */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center border-2 border-amber-500/30">
            <Lock className="w-12 h-12 text-amber-400" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-2xl md:text-3xl font-bold text-white text-center mb-4"
        >
          Chitragupt se baat karne ke liye
          <br />
          <span className="text-amber-400">Sign-in zaroori hai</span> 🔒
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-purple-200 text-center mb-8 max-w-sm leading-relaxed"
        >
          Aapki kundli aur guidance private rahegi.
          <br />
          Sign-in ke baad hi chat unlock hogi.
        </motion.p>

        {/* Sign In Button */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="w-full max-w-sm space-y-4"
        >
          <Button
            onClick={onSignIn || handleGoogleSignIn}
            className="w-full bg-white hover:bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 text-base shadow-lg hover:shadow-xl transition-all"
            data-testid="auth-required-google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Back Button */}
          <a
            href="https://jyotishpuraan.com"
            className="w-full border border-white/20 text-white hover:bg-white/10 py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors"
            data-testid="auth-required-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            ← Back to Home
          </a>
        </motion.div>

        {/* Guest Access Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mt-8 flex items-center gap-2 text-purple-300/60 text-sm"
        >
          <Shield className="w-4 h-4" />
          <span>Guest users can view Birth Chart + Daily Horoscope for free</span>
        </motion.div>
      </div>

      {/* Bottom Decoration */}
      <div className="h-20 bg-gradient-to-t from-black/20 to-transparent"></div>
    </div>
  );
};

export default AuthRequiredScreen;
